<template>
    <IPNav
        ref="navBarRef"
        class="text-[14px]"
        :navModuleType="NAV_MENU_TYPE.NEW_TASKS"
        :ipNavService="ipNavService"
    />
</template>

<script setup lang="ts">
import { IPNav, NAV_MENU_TYPE, createIpNavService } from '@patsnap-biz/ip-nav';
import { headerService } from '@/services/header';
import { useRouter } from 'vue-router';
import { watch, ref, onMounted, onUnmounted } from 'vue';
import psHttp from '@patsnap/common-ps-http';
import { useNavBar } from '@/composables/useNavBar';
import { useLoginSdk } from '@/composables/useLoginSdk';
import { EMPOWER_TYPE, CHANNEL_SOURCE, usePlan } from '@eureka/shared/src/login-sdk';
import { i18n } from '@/i18n';
import _ from 'lodash';
import { EUREKA_SERVICE_URI } from '@/constants/base-url';
import { useLastProjectInfo } from '@/composables/useLastProjectInfo';
import { track } from '@eureka/shared/src/utils/track';

const navBarRef = ref<any>();
const { value: plan } = usePlan();
const { currentReturnAgentType } = useLastProjectInfo();

const { openLoginDialog } = useLoginSdk(i18n, {
    from_page: EMPOWER_TYPE.AIR_HOME,
    channel_source: CHANNEL_SOURCE.AIR_EUREKA,
});

function openLogin() {
    openLoginDialog(false);
}

const router = useRouter();
const { isCollapsed } = useNavBar();

// 获取所有路由的 name 集合
const routeNames = new Set(
    router.getRoutes()
        .map(route => route.name)
        .filter(name => name) // 过滤掉没有 name 的路由
);

// 封装 psHttp 为符合 IAxiosClient 接口的对象
const httpClient = {
    get: (config: { url: string; params?: any; [key: string]: any }) =>
        psHttp({ ...config, method: 'GET' }).then(res => res.data),
    post: (config: { url: string; data?: any; [key: string]: any }) =>
        psHttp({ ...config, method: 'POST' }).then(res => res.data),
    delete: (config: { url: string; [key: string]: any }) =>
        psHttp({ ...config, method: 'DELETE' }).then(res => res.data),
};

const ipNavService = createIpNavService({
    headerService: headerService as any,
    httpClient,
    apiBaseUrl: EUREKA_SERVICE_URI,
    responsive: false,
    isActive: (context) => {
        const path = context.routePath;

        // 从 router 配置中查找匹配的路由,返回其 name
        const matchedRoute = router.getRoutes().find(route => {
            // 精确匹配
            if (route.path === path) {
                return true;
            }
            // 路径包含匹配(用于子路由)
            if (path.startsWith(route.path) && route.path !== '/') {
                return true;
            }
            return false;
        });

        return matchedRoute?.name as string || null;
    },
    onNavigate: (context) => {
        if (context.menu.type === NAV_MENU_TYPE.NEW_TASKS as string) {
            if(currentReturnAgentType.value) {
                context.preventDefault();
                window.location.href = `/ip/#/home?agent=${currentReturnAgentType.value}`;
            }
            return;
        }

        // 如果菜单的 type 属于项目路由名称,则阻止默认跳转,使用项目路由
        if (context.menu?.type && routeNames.has(context.menu.type)) {
            context.preventDefault();
            router.push({ name: context.menu.type });
        }
    },
    isShowUpgrade() {
        const isCn = headerService?.account?.siteRegion === 'CN';
        return !!(plan.value?.isFreemium && plan.value?.isPersonal) && !isCn;
    },
    onLogin: () => {
        openLogin();
    },
    onUpgrade: () => {
        track({
            event_type: 'SS Eureka Upgrade Page',
            source_type: 'upgrade_trigger',
            event_detail_str: JSON.stringify({
                product_line: 'eureka_ip',
                trigger_source: 'side_nav',
            }),
        });

        router.push({ name: 'Upgrade', query: { from_trigger: 'side_nav' } });
    },
});

watch(isCollapsed, (collapsed) => {
    ipNavService.toggleSidebar(!collapsed);
}, { immediate: true });


function handleClick(event: MouseEvent) {
    let target = event.target as HTMLElement;
    while (target && target !== navBarRef.value) {
        if (target.tagName === 'A') {
            const href = target.getAttribute('href');

            // 判断是否允许跳转
            if (!shouldAllowNavigation(href)) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            break;
        }
        target = target.parentElement as HTMLElement;
    }
}

function shouldAllowNavigation(href: string | null): boolean {
    if (!href) return true;
    if(href === '/ip') {
        router.push('/');
        return false;
    }
    return true;
}

// 组件挂载时添加事件监听
onMounted(() => {
    const rootElement = navBarRef.value?.$el || navBarRef.value;
    if (rootElement && import.meta.env.DEV) {
        rootElement.addEventListener('click', handleClick, true);
    }
});

// 组件卸载时移除事件监听
onUnmounted(() => {
    const rootElement = navBarRef.value?.$el || navBarRef.value;
    if (rootElement && import.meta.env.DEV) {
        rootElement.removeEventListener('click', handleClick, true);
    }
});
</script>

<style scoped>
/* IP-Nav 组件自带样式,通常不需要额外样式 */
</style>
